<template>
      <div class="d-flex align-center justify-center px-10 py-10"  v-if="featuresLoading" style="height:100%; min-height:400px">
        <v-progress-circular
            color="primary"
            indeterminate
        ></v-progress-circular>
    </div>
    <div v-else>
        <!-- Space Number -->
        <hb-form 
            label="Space Number" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_space_number')"
            :empty="!unit.number"
        >
            <template v-slot:display>
                {{ unit.number }}
            </template>
            <template v-slot:edit>
                <HbTextField
                    v-model="edited.number"
                    v-validate="'required|max:45'"
                    id="space_number"
                    name="space_number"
                    data-vv-as="Space Number"
                    :error-messages="errors.collect('space_number')"
                    placeholder="Enter Number"
                    autofocus
                />
            </template>
        </hb-form>

        <!-- Space Type -->
        <hb-form 
            label="Space Type" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="false" 
            :empty="!unit.unit_type_id"
        >
            <template v-slot:display>
                {{ getUnitTypeTitle(unit) }}
            </template>
            <template v-slot:edit>
                <HbTextField
                    v-model="edited.unit_type_id"
                    v-validate="'required|max:45'"
                    id="unit_type_id"
                    name="unit_type_id"
                    data-vv-as="Space Type"
                    :error-messages="errors.collect('unit_type_id')"
                    placeholder="Enter Space Type"
                    autofocus
                />
            </template>
        </hb-form>

        <!-- Available Date -->
        <hb-form 
            label="Available Date" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_available_date')"
            :empty="!unit.available_date"
        >
            <template v-slot:display>
                {{unit.available_date | formatDate}}
            </template>
            <template v-slot:edit>
                <hb-date-picker
                    @click:clear="edited.available_date = ''"
                    label="Date"
                    clearable
                    dense
                    id="available_date"
                    data-vv-name="available_date"
                    name="available_date"
                    data-vv-as="available date"
                    v-model="edited.available_date">
                </hb-date-picker>
            </template>
        </hb-form>

        <!-- Status -->
        <hb-form 
            label="Status" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_status')" 
            :empty="unit.status == null"
        >
            <template v-slot:display>
                {{ unit.status == 1 ? 'Online' : (unit.sec_status ? unit.sec_status : 'Offline') }}
            </template>
            <template v-slot:edit>
                <v-select
                    :items="statusOptions"
                    item-text="label"
                    item-value="id"
                    :hide-details="!errors.collect('status').length"
                    v-model.lazy="edited.status"
                    id="status"
                    data-vv-as="status"
                    name="status"
                    :error-messages="errors.collect('status')"
                    style="z-index:99999;"
                    class="input-font-size"
                ></v-select>

                <v-select
                    class="input-font-size mb-2"
                    v-if="show_secondary_status"
                    single-line
                    label="Secondary Status"
                    hide-details
                    :items="secondary_statuses"
                    v-model="edited.sec_status"
                    v-validate="'required'"
                    data-vv-name="Secondary Status"
                    data-vv-as="Secondary Status"
                    :error-messages="errors.collect('Secondary Status')"
                    >
                </v-select>

                <HbTextarea
                    class="mt-4"
                    :rows=6 
                    row-height="15" 
                    :clearable="true"
                    v-model="edited.reason"
                    v-validate="'required|max: 255'" 
                    :maxlength="255" 
                    data-vv-name="Reason"
                    data-vv-as="Reason" 
                    :error-messages="errors.collect('Reason')"
                    placeholder="Reason for status change"
                />
            </template>
        </hb-form>

        <!-- Access Area -->
        <hb-form 
            label="Access Area" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_access_area')"  
            :empty="!unit.area_name"
            v-show="accessAreasEnabled"
        >
            <template v-slot:display>
                {{ unit.area_name }}
            </template>
            <template v-slot:edit>
                <v-select
                    v-model.lazy="edited.area_id"
                    :items="accessAreas"
                    item-text="name"
                    item-value="id"
                    :hide-details="!errors.collect('area').length"
                    label="Access Area"
                    id="area"
                    data-vv-as="area"
                    name="area"
                    :error-messages="errors.collect('area')"
                    dense
                    style="z-index:99999;"
                    flat
                    solo
                    single-line
                    class="input-font-size"
                ></v-select>
            </template>
        </hb-form>

        <!-- Product -->
        <hb-form 
            label="Product" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="false" 
            :empty="!unit.product_id"
        >
            <template v-slot:display>
                {{unit.Product.name}}
            </template>
            <template v-slot:edit>
                <v-select
                    :items="products"
                    item-text="name"
                    item-value="id"
                    :hide-details="!errors.collect('product').length"
                    v-validate="'required|max:45'"
                    v-model.lazy="edited.product_id"
                    label="Product"
                    id="product"
                    data-vv-as="product"
                    name="product"
                    :error-messages="errors.collect('product')"
                    dense
                    style="z-index:99999;"
                    flat
                    solo
                    single-line
                    class="input-font-size"
                ></v-select>
            </template>
        </hb-form>

        <!-- Set Rate -->
        <hb-form 
            label="Set Rate" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_set_rate')"   
            :empty="!unit.set_rate"
        >
            <template v-slot:display>
                {{unit.set_rate | formatMoney}}
            </template>
            <template v-slot:edit>
                <v-text-field
                    name="set_rate"
                    id="set_rate"
                    placeholder="Rate"
                    prefix="$"
                    v-validate="'required|decimal:2|min_value:0|max_value:999999'"
                    v-model.lazy="edited.set_rate"
                    data-vv-as="unit number"
                    :error-messages="errors.collect('set_rate')"
                    :hide-details="!errors.collect('set_rate').length"
                    dense
                    flat
                ></v-text-field>
            </template>
        </hb-form>

        <!-- Sell Rate -->
        <hb-form 
            label="Sell Rate" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_sell_rate')"    
            :empty="!unit.price"
        >
            <template v-slot:display>
                {{unit.price | formatMoney}}
            </template>
            <template v-slot:edit>
                <v-text-field
                    name="price"
                    id="price"
                    placeholder="Price"
                    prefix="$"
                    v-validate="'required|decimal:2|min_value:0|max_value:999999'"
                    v-model.lazy="edited.price"
                    data-vv-as="unit number"
                    :error-messages="errors.collect('price')"
                    :hide-details="!errors.collect('price').length"
                    dense
                    flat
                ></v-text-field>
            </template>
        </hb-form>

        <!-- Description -->
        <hb-form 
            label="Description" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_description')"     
            :empty="!unit.description"
        >
            <template v-slot:display>
                <span v-html="$options.filters.nl2br(unit.description)"></span>
            </template>
            <template v-slot:edit>
                <v-textarea
                    id="description"
                    name="description"
                    label="Enter Description"
                    data-vv-as="description"
                    row="1"
                    v-model="edited.description"
                    v-validate="'max:1000'"
                    :error-messages="errors.collect('description')"
                    :hide-details="!errors.collect('description').length"
                    single-line
                    auto-grow
                    flat
                    solo
                    class="input-font-size"
                ></v-textarea>
            </template>
        </hb-form>

        <!-- Website Category -->
        <hb-form 
            label="Website Category" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_website_category')"      
            :empty="!unit.category_id"
        >
            <template v-slot:display>
                <span v-if="unit.category_id">{{unit.Category.name | capitalize}}</span>
            </template>
            <template v-slot:edit>
                <v-select
                    hide-details
                    :items="categories"
                    item-text="name"
                    item-value="id"
                    v-model.lazy="category"
                    label="Category"
                    id="category"
                    data-vv-as="category"
                    name="category"
                    style="z-index:99999;"
                    dense
                    flat
                    solo
                    single-line
                    return-object
                ></v-select>
            </template>
        </hb-form>

        <!-- Size -->
        <hb-form 
            label="Size" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_size')"     
            :empty="!unit.width"
        >
            <template v-slot:display>
                {{unit.width}}{{ !$t_flag('hide_dimensions_units') ? 'W': '' }} x {{unit.length}}{{ !$t_flag('hide_dimensions_units') ? 'L': '' }} x {{unit.height}}{{ !$t_flag('hide_dimensions_units') ? 'H': '' }}
            </template>
            <template v-slot:edit>
                <v-row class="ma-0">
                    <v-col md="4">
                        <v-text-field
                            type="text"
                            v-model="edited.width"
                            v-validate="'required|decimal:2|min_value:0|max_value:999'"
                            name="width"
                            id="width"
                            label="Width"
                            hide-details
                            :suffix="!$t_flag('hide_dimensions_units') ? $t('ft'): ''"
                            data-vv-as="Width"
                            :error-messages="errors.collect('width')"
                            @focus="$event.target.select()"
                        >
                        </v-text-field>
                    </v-col>
                    <v-col md="4">
                        <v-text-field
                            type="text"
                            v-model="edited.length"
                            v-validate="'required|decimal:2|min_value:0|max_value:999'"
                            name="length"
                            id="length"
                            label="Length"
                            hide-details
                            @focus="$event.target.select()"
                            :suffix="!$t_flag('hide_dimensions_units') ? $t('ft'): ''"
                            data-vv-as="Length"
                            :error-messages="errors.collect('length')"
                        >
                        </v-text-field>

                    </v-col>
                    <v-col md="4">
                        <v-text-field
                            type="text"
                            v-model="edited.height"
                            v-validate="'required|decimal:2|min_value:0|max_value:99'"
                            name="height"
                            id="height"
                            label="Height"
                            hide-details
                            :suffix="!$t_flag('hide_dimensions_units') ? $t('ft'): ''"
                            data-vv-as="Height"
                            :error-messages="errors.collect('height')"
                            @focus="$event.target.select()"
                        >
                        </v-text-field>
                    </v-col>

                    <v-col class="pt-0 pb-0">
                        <span v-show="errors.has('height')" class="status-block error-block field-error">{{ errors.first('height') }}</span>
                        <span v-show="errors.has('width')" class="status-block error-block field-error">{{ errors.first('width') }}</span>
                        <span v-show="errors.has('length')" class="status-block error-block field-error">{{ errors.first('length') }}</span>
                    </v-col>
                </v-row>
            </template>
        </hb-form>

        <!-- Door Size -->
        <hb-form 
            label="Door Size" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_door_size')"
            :empty="!unit['door width']"
        >
            <template v-slot:display>
                {{unit['door width']}}{{ !$t_flag('hide_dimensions_units') ? 'W': '' }} x {{unit['door height']}}{{ !$t_flag('hide_dimensions_units') ? 'H': '' }}
            </template>
            <template v-slot:edit>
                <v-row class="ma-0">

                    <v-col md="6">
                        <v-text-field
                            type="text"
                            v-model="edited['door width']"
                            name="door_width"
                            v-validate="'decimal:2|min_value:0|max_value:999'"
                            id="door_width"
                            label="Door Width"
                            hide-details
                            @focus="$event.target.select()"
                            :suffix="!$t_flag('hide_dimensions_units') ? $t('ft'): ''"
                            :error-messages="errors.collect('door_width')"
                            data-vv-as="Door Width"
                        >
                        </v-text-field>
                    </v-col>

                    <v-col md="6">
                        <v-text-field
                            type="text"
                            v-model="edited['door height']"
                            name="door_height"
                            v-validate="'decimal:2|min_value:0|max_value:999'"
                            id="door_height"
                            label="Door Height"
                            hide-details
                            :suffix="!$t_flag('hide_dimensions_units') ? $t('ft'): ''"
                            :error-messages="errors.collect('door_height')"
                            data-vv-as="Door Height"
                            @focus="$event.target.select()"
                        >
                        </v-text-field>
                    </v-col>

                    <v-col class="pt-0 pb-0">
                        <span v-show="errors.has('door_width')" class="status-block error-block field-error">{{ errors.first('door_width') }}</span>
                        <span v-show="errors.has('door_height')" class="status-block error-block field-error">{{ errors.first('door_height') }}</span>
                    </v-col>
                </v-row>
            </template>
        </hb-form>

        <!-- Space Floor -->
        <hb-form 
            label="Space Floor" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_space_floor')" 
            :empty="!unit.floor"
        >
            <template v-slot:display>
                {{unit.floor}}
            </template>
            <template v-slot:edit>
                <HbTextField
                    name="space_floor"
                    id="space_floor"
                    label="space floor"
                    v-model.lazy="edited.floor"
                    v-validate="'required|integer|min_value:-999|max_value:999'"
                    data-vv-as="space floor"
                    :error-messages="errors.collect('space_floor')"
                    placeholder="space floor"
                    dense
                    flat
                    solo
                />
            </template>
        </hb-form>

        <!-- Walk Through Order -->
        <hb-form 
            label="Walk Through Order" 
            @cancel="handleEmittedCancel" 
            @save="handleEmittedSave" 
            :active.sync="active"
            :editable="hasPermission('edit_walk_through_order')"  
            :empty="!unit.walkThroughSort"
        >
            <template v-slot:display>
                {{unit.walkThroughSort}}
            </template>
            <template v-slot:edit>
                <HbTextField
                    name="walk_through_order"
                    id="walk_through_order"
                    label="walk through order"
                    v-model.lazy="edited.walkThroughSort"
                    v-validate="'required|integer|min_value:1|max_value:99999999999'"
                    data-vv-as="walk through order"
                    :error-messages="errors.collect('walk_through_order')"
                    placeholder="walk through order"
                    dense
                    flat
                    solo
                />
            </template>
        </hb-form>
    </div>


</template>

<script type="text/babel">
    import api from '../../assets/api.js';
    import Status from '../includes/Messages.vue';
    import Loader from '../assets/CircleSpinner.vue';
    import Modal from '../assets/Modal.vue';
    import Dropdown from '../assets/Dropdown.vue';
    import Datepicker from 'vuejs-datepicker';
    import moment from 'moment';
    import RentRules from '../includes/RentRules.vue';
    import { EventBus } from '../../EventBus.js';
    import HbDatePicker from '../assets/HummingbirdDatepicker';
    import { mapActions, mapGetters } from 'vuex';
    import { notificationMixin } from  '@/mixins/notificationMixin.js';

    export default {
        name: "UnitDetails",
        data(){
            return {
                textFieldEditableInput:'',
                active:'',
                secondary_statuses:[
                    'To Clean',
                    'Company',
                    'Maintenance',
                    'Inactive'
                ],
                show_secondary_status:false,
                unit: {},
                editing: false,
                featuresLoading: false,

                edited: {
                    number: '',
                    description:'',
                    status: '',
                    featured: '',
                    floor: '',
                    price: '',
                    beds: '',
                    baths: '',
                    sqft: '',
                    pets: '',
                    parking: '',
                    furnished: '',
                    laundry: '',
                    category_id: '',
                    set_rate: '',
                    'year built': '',
                    'vehicle storage':'',
                    'unit type':'',
                    'door height': '',
                    'door width': '',
                    sec_status:null,
                    reason:null,
                    walkThroughSort: '',
                    unit_type_id: ''
                },
                products: [],
                categories:[],
                category:{},
                statusOptions: [
                    {id: 1, label: 'Online'},
                    {id: 0, label: 'Offline'}
                ],
                accessAreas: [],
                accessAreasEnabled: false,
                units: []

            }
        },
        props: ['unit_id', 'bulk_units', 'unit_number', 'reportType'],
        mixins:[
            notificationMixin
        ], 
        components:{
            Dropdown,
            Modal,
            Datepicker,
            Loader,
            Status,
            RentRules,
            HbDatePicker
        },
        async created(){
            await this.fetchData();
            this.fetchCategories();
            this.fetchProducts();
            this.mapData();
        },
        filters: {

        },
        computed: {
            ...mapGetters({
                hasPermission: 'authenticationStore/rolePermission', 
            }),
        },
        methods:{
            ...mapActions({
                getProductsByType: 'propertiesStore/getProductsByType'
            }),
            handleEmittedCancel(label){
                // do nothing
            },
            handleEmittedSave(label){
                let data = this.makePayload(label);
                data.scope = 'space_info';

                this.validate(this).then(status => {
                    if(!status) return;
                    api.put(this, api.PROPERTIES + this.unit.property_id + '/units/' + this.unit.id, data  ).then(async () => {
                        this.stopLoading(this.$options.name);
                        let change_area_error;
                        if (this.edited.area_id && this.edited.area_id != this.unit.area_id) {
                            await api.put(this, api.UNITS + this.unit.id + '/area', { area_id: this.edited.area_id }).catch((error) => {
                                change_area_error = error;
                            });
                        }

                        let report = this.$store.getters['reportStore/getAllStructures'][this.reportType];  
                        if(report) {
                            let report_id =  report.current_view !== 'template' ? (report.current_view || null) : null;
                            let viewBackup = JSON.parse( JSON.stringify(report.views.filter(x=> x.id === report_id)) );
                            let view = report.views.filter(x=> x.id === report_id);
                            if(view && view.length) {
                                viewBackup = viewBackup[0];
                                view = view[0];
                                view.filters.search = {
                                    unit_id:this.unit.id
                                }
                                view.filters.limit = 1;
                                view.filters.offset = 0;
                            }

                            let updatedData = await api.post(this, api.REPORTS + `types/${this.reportType}`, JSON.parse(JSON.stringify(view.filters)));
                            EventBus.$emit('unit_edited_single', updatedData.table_data[0]);

                            this.$store.commit('reportStore/setSearchParams', {
                                report_template: viewBackup.template,
                                report_id: report_id,
                                search:  viewBackup.filters.search,
                                offset: viewBackup.filters.offset,
                                limit: viewBackup.filters.limit,
                                page: viewBackup.filters.page
                            });
                        }
                        // BCT - Hb-72 Space Profile
                        if (change_area_error) {
                            this.showMessageNotification({type: 'error', description: change_area_error});
                        } else {
                            this.showMessageNotification({type: 'success', description: `Space info has been updated for Space ${this.unit_number}`});
                        }
                        
                        this.fetchData().then(() => {
                            this.resetEdited();
                            this.mapData();
                            this.editing = false;
                            this.stopLoading(this.$options.name);
                        });
                    }).catch(err => {
                        this.showMessageNotification({type: 'error', description: err});
                    });

                });
            },
            async fetchProducts(){
                var params = `${this.unit.property_id}/products?type=rent&category_type=rent`;
                this.products = await this.getProductsByType(params);
            },
            fetchCategories(){
                api.get(this, api.CATEGORIES, {unit_type_id: this.unit.unit_type_id}).then(r => {
                    this.categories = r.categories;
                });
            },
            fetchAccessAreas(){
                api.get(this, api.PROPERTIES + this.unit.property_id + "/areas?active=1").then(r => {
                    this.accessAreas = r;
                }).catch(err => {
                    this.showMessageNotification({ type: 'error', description: "No Access Areas have been set in Hummingbird. Please configure Access Areas or contact the Customer Support team." });
                });
            },
            resetEdited(){
                this.edited = {
                    number:'',
                    description:'',
                    featured:'',
                    status: '',
                    area_id: '',
                    price: '',
                    beds: '',
                    baths: '',
                    sqft: '',
                    pets: '',
                    parking: '',
                    furnished: '',
                    laundry: '',
                    category_id: '',
                    set_rate: '',
                    'year built': '',
                    'vehicle storage':'',
                    'unit type':'',
                    'door height': '',
                    'door width': '',
                    sec_status:'',
                    walkThroughSort: '',
                },
                this.reason = null;
            },
            fetchData(){
                this.featuresLoading = true;
                return api.get(this, api.UNITS + this.unit_id + '/features' ).then(async r=> {
                    this.unit = r.unit;
                    this.unit.status = this.unit.status;
                    this.unit.available_date = r.unit.available_date ?  moment(r.unit.available_date, 'YYYY-MM-DD').toDate(): null;

                    if(typeof this.unit.furnished != 'undefined'){
                        this.unit.furnished = +this.unit.furnished;
                    }
                    await api.get(this, api.PROPERTIES + this.unit?.property_id + "/property-area-management").then(async results => {
                        this.accessAreasEnabled = results?.property_area_configuration.access_hours_upgrade === 1;
                        this.fetchAccessAreas();
                    }).catch(error => {
                        this.accessAreasEnabled = false;
                    });
                }).finally(() => this.featuresLoading = false)
            },
            mapData(){
                console.log("this.unit");
                console.log("this.unit");
                console.log("this.unit");
                console.log("this.unit");
                console.log(this.unit);

                this.category = this.unit?.category_id;
                this.edited.number = this.unit?.number || '',
                this.edited.available_date = this.unit?.available_date || '',
                this.edited.status = this.unit?.status == 1 ? 1 : 0 || 0 ,
                this.edited.sec_status = this.unit?.sec_status || 'Inactive',
                this.edited.reason = null,
                this.edited.set_rate = this.unit?.set_rate || 0,
                this.edited.price = this.unit?.price || 0,
                this.edited.description = this.unit?.description || '',
                this.edited.width = this.unit?.width || 0,
                this.edited.length = this.unit?.length || 0,
                this.edited.height = this.unit?.height || 0,
                this.edited['door width'] = this.unit['door width'] || 0,
                this.edited['door height'] = this.unit['door height'] || 0,
                this.edited.floor = this.unit?.floor || 0,
                this.edited.walkThroughSort = this.unit?.walkThroughSort || 0,
                this.edited.product_id = this.unit?.product_id || 0,
                this.edited.area_id = this.unit?.area_id || 0


                console.log("this.edited");
                console.log("this.edited");
                console.log("this.edited");
                console.log("this.edited");
                console.log(this.edited);
            },
            makePayload(label){
                let payload = {};
                switch(label){
                    case 'Space Number' :
                        payload.number = this.edited.number;
                        break;
                    case 'Space Type' :
                        payload.unit_type_id = this.edited.unit_type_id;
                        break;
                    case 'Available Date' :
                        payload.available_date = moment(this.edited.available_date).startOf('day').format('YYYY-MM-DD');
                        break;
                    case 'Status' :
                        payload.status = this.edited.status;
                        if(this.edited.status == 0) payload.sec_status = this.edited.sec_status;
                        payload.reason = this.edited.reason;
                        break;
                    case 'Access Area' :
                        payload.area_id = this.edited.area_id;
                        break;
                    case 'Product' :
                        payload.product_id = this.edited.product_id;
                        break;
                    case 'Set Rate' :
                        payload.set_rate = this.edited.set_rate;
                        break;
                    case 'Sell Rate' :
                        payload.price = this.edited.price;
                        break;
                    case 'Description' :
                        payload.description = this.edited.description;
                        break;
                    case 'Website Category':
                        payload.category_id = this.category.id;
                        break;
                    case 'Size' :
                        payload.width = this.edited.width;
                        payload.length = this.edited.length;
                        payload.height = this.edited.height;
                        payload.unit_type_id = this.unit.unit_type_id;
                        break;
                    case 'Door Size' :
                        payload['door width'] = this.edited['door width'];
                        payload['door height'] = this.edited['door height'];
                        payload.unit_type_id = this.unit.unit_type_id;
                        break;
                    case 'Space Floor' :
                        payload.floor = this.edited.floor;
                        payload.unit_type_id = this.unit.unit_type_id;
                        break;
                    case 'Walk Through Order' :
                        payload.walkThroughSort = this.edited.walkThroughSort;
                        break;
                    default :
                        // do nothing
                        break;
                }
                return payload;
            }
        },
        watch:{
            unit_id(){
                this.resetEdited();
                this.editing = false;
                this.fetchData();
            },

            "errors.items"(val) {
                let sizeField = [];
                if(val && val.length) {
                    const errorMsg = [];
                    val.forEach((item) => {
                        if(
                            (item.field === "Reason" || item.field === "Secondary Status" ) 
                            && 
                            item.rule === 'required'
                        ){
                            errorMsg.push(item.msg)
                        }
                        if (item.field === "space_number" && item.rule === "required" ) {
                            errorMsg.push("Please enter a valid space number.")
                        }
                        if (item.field === "price" && ["required", "decimal"].includes(item.rule) ) {
                            errorMsg.push("Please enter a valid price amount.")
                        }
                        if (item.field === "set_rate" && ["required", "decimal"].includes(item.rule) ) {
                            errorMsg.push("Please enter a valid Set Rate amount.")
                        }

                        if (item.field === "width" && ["required", "decimal"].includes(item.rule)) {
                            sizeField.push('width');
                        }
                        
                        if (item.field === "length" && ["required", "decimal"].includes(item.rule) ) {
                            sizeField.push('length');
                        }
                        
                        if (item.field === "height" && ["required", "decimal"].includes(item.rule) ) {
                            sizeField.push('height');
                        }

                        if (item.field === "door_height" && [ "decimal"].includes(item.rule) ) {
                            sizeField.push('door_height');
                        }

                        if (item.field === "door_width" && [ "decimal"].includes(item.rule) ) {
                            sizeField.push('door_width');
                        }

                        if (item.field === "space_floor" && (["required", "integer", "max_value", "min_value"].includes(item.rule)) ) {
                            errorMsg.push("Space floor must be an integer number in range -999 to 999")
                        }

                        if (item.field === "walk_through_order" && ["required", "integer", "min_value", "max_value"].includes(item.rule)) {
                            errorMsg.push("Please enter a valid Walk Through Order number Positive Integer Greater than 0.");
                        }

                    })

                    if (sizeField.length) {
                        errorMsg.push(`Please enter a valid size value for ${sizeField.join(', ')}.`);
                    }

                    let desc = 'There are errors in your form, correct them before continuing.';
                    this.showMessageNotification({type: 'error', description: desc, list: errorMsg});

                }
            },
            
            'edited.status'(val){
                this.show_secondary_status = val == 0 ? true : false;
            }
        }
    }
</script>

<style scoped>
    .interaction-element span.icon{
        display:none;
        cursor: pointer;
        font-size: 12px;
        line-height: 12px;
        padding: 0;
        color: #ccc;
    }
    .interaction-element span.icon.delete:hover{
        color: #D91E18;
    }

    .interaction-element:hover span.icon{
        display:inline;
    }

    .slide-out-section.editing .interaction-text{
        padding-top: 10px;
        padding-bottom: 0px;
    }
    .interaction-details {
        padding: 12px 24px;
        background-color: #F4F6F8;
    }
    .interaction-text {
        padding: 12px;
    }

    .interaction {
        border-bottom: 1px solid #dce8ef;
    }
    .alignleft {
        float: left;
    }
    .alignright {
        float: right;
    }


</style>
